import React, { useCallback, useEffect, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import FitTable from '../shared/fit_table';
import LoadFailed from '../shared/load_failed';
import { SelectFilter } from '../shared/controls';
import TpDialog from '../shared/tp_dialog';
import { fetch_close_versions_teacher_month } from '../api';
import { PAYABLE, changed_lines, cv_error_view, diff_tone, teacher_month_view } from './adapters';
import './close_versions.css';
import { value_align } from '../shared/table_layout';
import DownloadCsvButton from '../../../util_components/csv_export/download_csv_button';
import { cv_csv_name, versions_csv_columns } from './csv_columns';

/*
 * one teacher-month's versions (v1, v2, …, plus "Live now" while reopened), each against the one
 * before it, per component. Wide: every version in one table; narrow: one pair at a time.
 */
const LOAD_FAILED_TEXT = 'Could not load the versions of this teacher-month.';

const SOURCE_HINTS = {
  rebuilt_from_history: 'Rebuilt from the last recalculation before this close.',
  net_only: 'Only the net payable was recorded for this close; the breakdown is not available.'
};

const cell_class = (change, key) =>
  change && change.lines[key].changed ? 'tp-cv-changed' : undefined;

const TONE_CLASS = { pos: 'tp-pos', neg: 'tp-neg' };

const WhatChanged = ({ change }) => {
  if (!change) return <span className="tp-hint">First close</span>;
  const lines = changed_lines(change);
  if (!lines.length)
    return <span className="tp-hint">No change against {change.baseline_label}</span>;
  return (
    <>
      <div className="tp-cv-changes-hd">Change vs {change.baseline_label}</div>
      <ul className="tp-cv-changes" aria-label={`Changed against ${change.baseline_label}`}>
        {lines.map((l) => (
          <li key={l.key}>
            <span>{l.label}</span> <span className={TONE_CLASS[l.tone]}>{l.text}</span>
          </li>
        ))}
      </ul>
    </>
  );
};

const VersionCard = ({ col }) => (
  <div className="tp-cv-version" data-version={col.key}>
    <div className="tp-cv-version-hd">
      <b>{col.label}</b>
      <span className="tp-cv-source">{col.source_label}</span>
    </div>
    {SOURCE_HINTS[col.source] ? <div className="tp-hint">{SOURCE_HINTS[col.source]}</div> : null}
    {col.meta.map((line) => (
      <div key={line} className="tp-cv-meta">
        {line}
      </div>
    ))}
    <WhatChanged change={col.change} />
  </div>
);

// A version's value with, when it moved, its change against the version before it underneath.
const VersionCell = ({ col, line }) => {
  const change = col.change ? col.change.lines[line.key] : null;
  return (
    <td
      data-label={col.label}
      className={['tp-cv-cell tp-col-align-right', cell_class(col.change, line.key)]
        .filter(Boolean)
        .join(' ')}
    >
      {/* "Not available" is an absence, not a figure: muted. */}
      <span className={`tp-cv-v${value_align(col.values[line.key]) === 'left' ? ' tp-muted' : ''}`}>
        {col.values[line.key]}
      </span>
      {change && change.changed ? (
        <span className="tp-cv-d">
          <span className={TONE_CLASS[diff_tone(change.diff, line.key)]}>{change.text}</span> vs{' '}
          {col.change.baseline_label}
        </span>
      ) : null}
    </td>
  );
};

// Every version side by side, a line per row; with a pair to show, narrow screens use PairView.
const AllVersionsTable = ({ view }) => (
  <FitTable className={view.pairs.length ? 'tp-cv-full' : ''}>
    <table className="tp-table tp-cv-table">
      <thead>
        <tr>
          <th scope="col">Line</th>
          {view.columns.map((c) => (
            <th key={c.key} scope="col" className="tp-col-align-right">
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {view.lines.map((l) => (
          <tr key={l.key} className={l.key === PAYABLE.key ? 'tp-cv-total' : undefined}>
            <th scope="row">{l.label}</th>
            {view.columns.map((c) => (
              <VersionCell key={c.key} col={c} line={l} />
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </FitTable>
);

// Right-aligned values; a word ("Not available", "No change") may wrap and reads muted.
const PairCell = ({ label, value, changed, tone }) => {
  const money = value_align(value) === 'right';
  return (
    <td data-label={label} className={['tp-col-align-right', changed].filter(Boolean).join(' ')}>
      {money ? (
        <span className={['tp-nowrap', TONE_CLASS[tone]].filter(Boolean).join(' ')}>{value}</span>
      ) : (
        <span className="tp-muted">{value}</span>
      )}
    </td>
  );
};

const PairView = ({ view }) => {
  const [pair_id, setPairId] = useState(view.pairs.length ? view.pairs[0].id : '');
  const pair = view.pairs.find((p) => p.id === pair_id) || view.pairs[0];
  if (!pair) return null;
  const { before, after } = pair;
  return (
    <div className="tp-cv-pair">
      {view.pairs.length > 1 ? (
        <div className="tp-cv-pairpick">
          <SelectFilter
            id="tp-cv-pair"
            label="Compare"
            aria_label="Compare"
            value={pair.id}
            options={view.pairs.map((p) => ({
              value: p.id,
              label: `${p.after.label} with ${p.before.label}`
            }))}
            active={false}
            onChange={setPairId}
          />
        </div>
      ) : null}
      <FitTable>
        <table className="tp-table tp-cv-pair-table">
          <thead>
            <tr>
              <th scope="col">Line</th>
              <th scope="col" className="tp-col-align-right">
                {before.label}
              </th>
              <th scope="col" className="tp-col-align-right">
                {after.label}
              </th>
              <th scope="col" className="tp-col-align-right">
                Change
              </th>
            </tr>
          </thead>
          <tbody>
            {view.lines.map((l) => (
              <tr key={l.key} className={l.key === PAYABLE.key ? 'tp-cv-total' : undefined}>
                <th scope="row">{l.label}</th>
                <PairCell label={before.label} value={before.values[l.key]} />
                <PairCell
                  label={after.label}
                  value={after.values[l.key]}
                  changed={cell_class(after.change, l.key)}
                />
                <PairCell
                  label="Change"
                  value={after.change.lines[l.key].text}
                  changed={cell_class(after.change, l.key)}
                  tone={diff_tone(after.change.lines[l.key].diff, l.key)}
                />
              </tr>
            ))}
          </tbody>
        </table>
      </FitTable>
    </div>
  );
};

const VersionsDialog = ({
  pay_month,
  month_label,
  teacher_uuid,
  teacher_name,
  on_close,
  on_focus_lost,
  on_error
}) => {
  const [state, setState] = useState('loading'); // loading | ready | error
  const [view, setView] = useState(null);
  const [error_text, setErrorText] = useState('');
  const seq = useRef(0);
  // The callers' callbacks are read through refs so a re-render never re-reads the versions.
  const on_close_ref = useRef(on_close);
  on_close_ref.current = on_close;
  const on_error_ref = useRef(on_error);
  on_error_ref.current = on_error;

  const load = useCallback(() => {
    seq.current += 1;
    const mine = seq.current;
    setState('loading');
    fetch_close_versions_teacher_month(pay_month, teacher_uuid)
      .then((data) => {
        if (mine !== seq.current) return;
        setView(teacher_month_view(data));
        setState('ready');
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        const v = cv_error_view(e, LOAD_FAILED_TEXT);
        if (v.kind === 'permission') {
          on_error_ref.current(e, LOAD_FAILED_TEXT);
          on_close_ref.current();
          return;
        }
        setErrorText(v.text);
        setState('error');
      });
  }, [pay_month, teacher_uuid]);

  useEffect(() => {
    load();
  }, [load]);

  const name = (view && view.teacher_name) || teacher_name || 'Teacher';
  const title = `${name} · ${month_label}`;

  return (
    <TpDialog title={title} on_close={on_close} on_focus_lost={on_focus_lost} size="xl">
      <div className="tp-cv-dialog">
        {state === 'loading' ? <CustomLoading /> : null}
        {state === 'error' ? <LoadFailed text={error_text} on_retry={load} /> : null}
        {state === 'ready' && !view.columns.length ? (
          <div className="tp-empty">
            No close is recorded for {name} in {month_label}.
          </div>
        ) : null}
        {state === 'ready' && view.columns.length ? (
          <>
            <div className="tp-cv-versions">
              {view.columns.map((c) => (
                <VersionCard key={c.key} col={c} />
              ))}
            </div>
            <div className="tp-cv-csv">
              <DownloadCsvButton
                columns={versions_csv_columns(view, teacher_uuid)}
                rows={view.lines}
                file_name={() => cv_csv_name(`versions-${teacher_uuid}`, pay_month)}
              />
            </div>
            <AllVersionsTable view={view} />
            <PairView view={view} />
          </>
        ) : null}
      </div>
    </TpDialog>
  );
};

export default VersionsDialog;
